import { useStoreState } from '@ariakit/react'
import isMatch from 'lodash-es/isMatch'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Dropdown } from '@syconium/little-miss-figgy'

import { PREFERENCE_SUPPORTED_LOCATIONS } from '../../../../../app/_components/pages/myfigs/preferences/constants'
import { useUpdateEmbroideryPreferences } from '../../../../../app/_hooks/useEmbroideryPreferences'
import { useAuthentication } from '../../../../../app/_providers/AuthenticationProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useEmbroideryForm } from '../../../../../containers/EmbroideryContainer/useEmbroideryForm'
import { useEmbroideryV2Flag } from '../../../../../containers/EmbroideryContainer/useEmbroideryV2Flag'
import { EMBROIDERY_TEXT_MAX_CHARS } from '../../../../../containers/EmbroideryContainer/utils/unorganized'
import { PreferencesToggle } from '../../../Modal/primitives/shared-form-components'
import { EmbroideryOptionWrapper, FormBody } from '../../../Modal/primitives/shared-form-styles'
import { SubmissionButtons } from '../../../Modal/primitives/SubmissionButtons/SubmissionButtons'
import { useValidPlacementOptionsForLegacyFlow } from '../../../unorganized'
import { StyledHr } from '../DTCLogoEmbroideryForm/EmbroideryLogoSetup/styles'
import { TextPreviewItem } from '../PreviewItems/TextPreviewItem'
import { buildTextPreferenceQuery } from '../utils'

import { Font } from './FormBody/Font'
import * as Name from './FormBody/Name'
import { NameColor } from './FormBody/NameColor'
import { TextPlacement } from './FormBody/TextPlacement'
import { TextPreferencesToggleWrapper } from './styles'
import { useValidationSchema } from './useValidationSchema'

export const TextEmbroideryForm = () => {
  const {
    productEmbroideryConfig,
    textEmbroideryConfig,
    selectedFormKind,
    hiddenFormFields,
    disabledFormFields,
    embroideryPriceRangeByType,
    isB2bEmbroidery,
    savedEmbroideryDefaultFormValues,
    withPricing,
    productColorHex,
  } = useEmbroidery()

  const { isLoggedIn } = useAuthentication()
  /**
   * Temp blocking of MyFigs Preferences for the embroidery multi-placement
   * this should be cleaned up as a part of the Preferences/Multiplacement integration LFE-125
   * */
  const preferencesEnabled = !useEmbroideryV2Flag({ skip: false })
  const TEXT = savedEmbroideryDefaultFormValues?.TEXT

  const onToggle = () => {
    setSavePreferencesChecked(!savePreferencesChecked)
  }

  const hideFields = hiddenFormFields?.TEXT ?? []
  const disableFields = disabledFormFields?.TEXT ?? []

  const maxCharacterCount =
    productEmbroideryConfig?.options.text?.characterLimit ?? EMBROIDERY_TEXT_MAX_CHARS
  const lineLimit = productEmbroideryConfig?.options.text?.lineLimit ?? 1
  const validationSchema = useValidationSchema({
    textMaxChars: maxCharacterCount,
    textEmbroideryConfig,
  })
  const { form, resetForm, isDirty } = useEmbroideryForm('TEXT', {
    validationSchema,
    createEmbroideryOptions: values => ({
      textColor: values.textColor,
      textFirstLine: values.firstLine,
      textFont: values.font,
      textPlacement: values.textPlacement,
      textSecondLine: values.secondLine,
    }),
    previewItem: values => <TextPreviewItem values={values} />,
  })

  const formValues = useStoreState(form, s => s.values)
  const formErrors = useStoreState(form, s => s.errors)
  const secondLineError = useStoreState(form, () => form.getError('secondLine'))

  const placementOptions = useValidPlacementOptionsForLegacyFlow(
    productEmbroideryConfig?.options.text?.placements
  )

  // Don't show save preferences toggle if the placements a shopper can pick include locations unsupported by MyFigs
  const supportsPreferenceLocation = Boolean(
    placementOptions?.length &&
      placementOptions.every(p => PREFERENCE_SUPPORTED_LOCATIONS.has(p.location))
  )

  /** For "save preferences" toggle. Once the form is dirtied
   * we uncheck, but don't recheck if form becomes 'undirty'
   */
  const hasFormDirtied = useRef<boolean>(false)
  const hasInitialPreferences: boolean = useMemo(() => {
    // isMatch does partial comparison, because the form
    // might have more values than preference options (textColor), but
    // preference options will always need to match to satisfy this
    return Boolean(TEXT?.firstLine && isMatch(formValues, TEXT))
  }, [TEXT, formValues])

  const [savePreferencesChecked, setSavePreferencesChecked] =
    useState<boolean>(hasInitialPreferences)

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const { updateTextEmbroideryPreferences } = useUpdateEmbroideryPreferences()

  const onSubmit = async (e: React.FormEvent) => {
    // stopPropagation prevents outer forms from also submitting
    e.preventDefault()
    e.stopPropagation()

    const hasErrors = Object.values(formErrors).some(Boolean)
    if (savePreferencesChecked && !hasErrors && formValues.firstLine.length > 0 && isDirty) {
      const query = buildTextPreferenceQuery(formValues)
      await updateTextEmbroideryPreferences(query)
    }

    void form.submit()
  }

  useEffect(() => {
    if (TEXT?.firstLine && !hasFormDirtied.current && isDirty) {
      hasFormDirtied.current = true
      setSavePreferencesChecked(false)
    }
  }, [isDirty, TEXT?.firstLine, setSavePreferencesChecked])

  return (
    <FormBody onSubmit={onSubmit} data-testid='text-embroidery-form'>
      {!hideFields.includes('firstLine') && (
        <Name.Wrapper disabled={disableFields.includes('firstLine')} label='first-line-text'>
          <Name.Input
            ariaLabelledby='first-line-text'
            name='firstLine'
            form={form}
            disabled={disableFields.includes('firstLine')}
            maxCharacterCount={maxCharacterCount}
            placeholder={fixtures.textOption.firstLinePlaceholder}
          />
        </Name.Wrapper>
      )}
      {!hideFields.includes('secondLine') && lineLimit >= 2 && (
        <Name.Wrapper
          disabled={disableFields.includes('secondLine')}
          label='second-line-text'
          title={fixtures.textOption.secondLinePlaceholder}
        >
          {(textEmbroideryConfig?.secondLineOptions ?? []).length > 0 ? (
            <Dropdown
              data-testid='second-line-dropdown'
              isInvalid={Boolean(secondLineError)}
              messageWhenInvalid={secondLineError ?? undefined}
              onChange={sel => form.setValue('secondLine', sel)}
              options={
                textEmbroideryConfig?.secondLineOptions
                  ? ['', ...textEmbroideryConfig.secondLineOptions]
                  : []
              }
              renderOptionText={option =>
                option === '' ? fixtures.textOption.secondLineDropdownEmptyOption : option
              }
              renderSelectionText={selection =>
                selection === '' ? fixtures.textOption.secondLineDropdownEmptyOption : selection
              }
              placeholderText={fixtures.textOption.secondLineDropdownPlaceholder}
              selection={formValues.secondLine}
              variant='box'
              hidePlaceholderAsOption
            />
          ) : (
            <Name.Input
              ariaLabelledby='second-line-text'
              name='secondLine'
              form={form}
              disabled={disableFields.includes('secondLine')}
              maxCharacterCount={maxCharacterCount}
              placeholder={fixtures.textOption.secondLinePlaceholder}
            />
          )}
        </Name.Wrapper>
      )}

      {!hideFields.includes('textPlacement') && (
        <TextPlacement
          labelId='text-placement-for-text-only'
          placementOptions={placementOptions}
          form={form}
          kind={selectedFormKind}
          disabled={disableFields.includes('textPlacement')}
        />
      )}

      {!hideFields.includes('textColor') && (
        <NameColor
          form={form}
          // Temporary unblocking of colors for DTC. Remove this in LFE-187
          colorRestrictions={!isB2bEmbroidery ? [] : productEmbroideryConfig?.colorRestrictions}
          disabled={disableFields.includes('textColor')}
          productColorHex={productColorHex}
        />
      )}

      {!hideFields.includes('font') && (
        <Font
          labelId='font-selector-for-text-only'
          form={form}
          // Temporary unblocking of fonts for DTC. Remove this in LFE-188
          fontRestrictions={!isB2bEmbroidery ? [] : productEmbroideryConfig?.fontRestrictions}
          disabled={disableFields.includes('font')}
        />
      )}
      {preferencesEnabled && isLoggedIn && !isB2bEmbroidery && supportsPreferenceLocation && (
        <TextPreferencesToggleWrapper>
          <PreferencesToggle
            checked={savePreferencesChecked}
            onToggle={onToggle}
            switchLabel={fixtures.preferences.textPreferenceToggleLabel}
            tooltipText={fixtures.preferences.textPreferenceTooltip}
            tooltipAriaLabel={fixtures.preferences.textPreferenceTooltipAriaLabel}
          />
        </TextPreferencesToggleWrapper>
      )}
      <EmbroideryOptionWrapper>
        <StyledHr />
        <SubmissionButtons
          displayPrice={embroideryPriceRangeByType.TEXT?.effectivePrice.displayValue}
          withPricing={withPricing}
          onReset={() => {
            setSavePreferencesChecked(false)
            resetForm()
          }}
        />
      </EmbroideryOptionWrapper>
    </FormBody>
  )
}
